import { ref } from 'vue'
import type { CognitoUser } from 'amazon-cognito-identity-js'

interface NewPasswordChallenge {
  user: CognitoUser
  userAttributes: any
  requiredAttributes: any[]
  resolve: (session: any) => void
  reject: (error: Error) => void
}

const isDialogOpen = ref(false)
const currentChallenge = ref<NewPasswordChallenge | null>(null)

export function useNewPasswordDialog() {
  function openDialog(challenge: NewPasswordChallenge) {
    currentChallenge.value = challenge
    isDialogOpen.value = true
  }

  function closeDialog() {
    isDialogOpen.value = false
    currentChallenge.value = null
  }

  async function submitNewPassword(newPassword: string): Promise<void> {
    if (!currentChallenge.value) {
      throw new Error('No active password challenge')
    }

    const { user, resolve, reject } = currentChallenge.value

    return new Promise((resolveSubmit, rejectSubmit) => {
      user.completeNewPasswordChallenge(
        newPassword,
        {}, // optional map of other attributes to set
        {
          onSuccess: (session) => {
            console.log('Password changed successfully, user signed in', session)
            closeDialog()
            resolve(session)
            resolveSubmit()
          },
          onFailure: (err) => {
            console.error('Failed to set new password:', err)
            rejectSubmit(err)
            // Don't close dialog or reject main promise yet - let user retry
          },
        }
      )
    })
  }

  function cancelDialog() {
    if (currentChallenge.value) {
      currentChallenge.value.reject(new Error('Password change cancelled by user'))
    }
    closeDialog()
  }

  return {
    isDialogOpen,
    currentChallenge,
    openDialog,
    closeDialog,
    submitNewPassword,
    cancelDialog,
  }
}
